import { useEffect, useState } from "react";
import {
  Box,
  SimpleGrid,
  Badge,
  VStack,
  HStack,
  Text,
  Heading,
  Icon,
  Flex,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { getLocalStorageItem } from "../Utils/Helper";
import { useTheme } from "next-themes";
import { setDynamicTitle } from "../Utils/dynamicTitleManager";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import {
  FaUsers,
  FaDollarSign,
  FaExchangeAlt,
  FaCheckCircle,
  FaClock,
  FaChartLine,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";

export const Dashboard = () => {
  // Use dynamic title management
  useEffect(() => {
    setDynamicTitle("Dashboard");
  }, []);

  const { resolvedTheme } = useTheme();
  const loading = useSelector((state) => state?.GetWhiteLabel?.loading);
  const whitelabelLocalStorage = getLocalStorageItem("whitelabelData");

  // Dummy data for statistics
  const statsData = [
    {
      label: "Total Users",
      value: "1,234",
      change: 12.5,
      isPositive: true,
      icon: FaUsers,
      color: "blue",
    },
    {
      label: "Total Revenue",
      value: "$45,678",
      change: 8.2,
      isPositive: true,
      icon: FaDollarSign,
      color: "green",
    },
    {
      label: "Active Trades",
      value: "567",
      change: 3.1,
      isPositive: false,
      icon: FaExchangeAlt,
      color: "purple",
    },
    {
      label: "Pending KYC",
      value: "89",
      change: 5.4,
      isPositive: true,
      icon: FaCheckCircle,
      color: "orange",
    },
  ];

  // Dummy data for user growth chart
  const userGrowthData = [
    { name: "Jan", users: 400, revenue: 2400 },
    { name: "Feb", users: 300, revenue: 2210 },
    { name: "Mar", users: 550, revenue: 3250 },
    { name: "Apr", users: 480, revenue: 2900 },
    { name: "May", users: 690, revenue: 4100 },
    { name: "Jun", users: 800, revenue: 5200 },
    { name: "Jul", users: 950, revenue: 6100 },
  ];

  // Dummy data for pie chart (user distribution)
  const userDistributionData = [
    { name: "Retail Users", value: 450, color: "#3182ce" },
    { name: "IB Partners", value: 120, color: "#805ad5" },
    { name: "Corporate", value: 85, color: "#38a169" },
    { name: "Pending", value: 45, color: "#dd6b20" },
  ];

  // Dummy data for recent transactions
  const recentTransactions = [
    {
      id: 1,
      user: "John Doe",
      type: "Deposit",
      amount: "$5,000",
      status: "Completed",
      date: "2024-03-20",
    },
    {
      id: 2,
      user: "Jane Smith",
      type: "Withdrawal",
      amount: "$2,500",
      status: "Pending",
      date: "2024-03-20",
    },
    {
      id: 3,
      user: "Mike Johnson",
      type: "Trade",
      amount: "$1,200",
      status: "Completed",
      date: "2024-03-19",
    },
    {
      id: 4,
      user: "Sarah Williams",
      type: "Deposit",
      amount: "$10,000",
      status: "Completed",
      date: "2024-03-19",
    },
    {
      id: 5,
      user: "Robert Brown",
      type: "Withdrawal",
      amount: "$3,800",
      status: "Processing",
      date: "2024-03-18",
    },
  ];

  // Dummy data for top performers
  const topPerformers = [
    { rank: 1, name: "Alex Thompson", profit: "$12,450", trades: 145 },
    { rank: 2, name: "Maria Garcia", profit: "$10,890", trades: 132 },
    { rank: 3, name: "James Wilson", profit: "$9,670", trades: 118 },
    { rank: 4, name: "Linda Martinez", profit: "$8,540", trades: 105 },
    { rank: 5, name: "David Anderson", profit: "$7,320", trades: 98 },
  ];

  return (
    <VStack spacing={6} align="stretch" p={6}>
      {/* Page Header */}
      <Box mb={4}>
        <Heading size="lg" mb={2}>
          Dashboard Overview
        </Heading>
        <Text color="gray.500" fontSize="sm">
          Welcome back! Here's what's happening today.
        </Text>
      </Box>

      {/* Statistics Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
        {statsData.map((stat, index) => (
          <Box
            key={index}
            bg="white"
            p={5}
            borderRadius="xl"
            boxShadow="md"
            _hover={{ boxShadow: "lg", transform: "translateY(-2px)" }}
            transition="all 0.2s"
          >
            <HStack justify="space-between" align="start">
              <Box>
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    {stat.label}
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" mt={1}>
                    {stat.value}
                  </Text>
                  <HStack spacing={1} mt={1}>
                    <Icon
                      as={stat.isPositive ? FaArrowUp : FaArrowDown}
                      boxSize={3}
                      color={stat.isPositive ? "green.500" : "red.500"}
                    />
                    <Text
                      fontSize="sm"
                      color={stat.isPositive ? "green.500" : "red.500"}
                    >
                      {stat.change}%
                    </Text>
                    <Text fontSize="xs" color="gray.400">
                      vs last month
                    </Text>
                  </HStack>
                </Box>
              </Box>
              <Box
                p={3}
                borderRadius="full"
                bg={`${stat.color}.100`}
                color={`${stat.color}.600`}
              >
                <Icon as={stat.icon} boxSize={6} />
              </Box>
            </HStack>
          </Box>
        ))}
      </SimpleGrid>

      {/* Charts Section */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
        {/* User Growth & Revenue Chart */}
        <Box bg="white" p={5} borderRadius="xl" boxShadow="md">
          <HStack justify="space-between" mb={4}>
            <Heading size="md">Growth Analytics</Heading>
            <Badge colorScheme="blue">Last 7 Months</Badge>
          </HStack>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={userGrowthData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="users"
                stroke="#3182ce"
                strokeWidth={2}
                name="Users"
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#48bb78"
                strokeWidth={2}
                name="Revenue ($)"
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        {/* User Distribution Pie Chart */}
        <Box bg="white" p={5} borderRadius="xl" boxShadow="md">
          <Heading size="md" mb={4}>User Distribution</Heading>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={userDistributionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name}: ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {userDistributionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <HStack justify="center" mt={4} flexWrap="wrap">
            {userDistributionData.map((item, index) => (
              <HStack key={index} spacing={2}>
                <Box w={3} h={3} borderRadius="sm" bg={item.color} />
                <Text fontSize="sm">{item.name}</Text>
              </HStack>
            ))}
          </HStack>
        </Box>
      </SimpleGrid>

      {/* Tables Section */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
        {/* Recent Transactions */}
        <Box bg="white" p={5} borderRadius="xl" boxShadow="md">
          <HStack justify="space-between" mb={4}>
            <Heading size="md">Recent Transactions</Heading>
            <Badge colorScheme="green">Live</Badge>
          </HStack>
          <Box overflowX="auto">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', color: '#4a5568' }}>User</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', color: '#4a5568' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', color: '#4a5568' }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600', color: '#4a5568' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.map((transaction) => (
                  <tr key={transaction.id} style={{ borderBottom: '1px solid #edf2f7' }}>
                    <td style={{ padding: '12px' }}>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">{transaction.user}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {transaction.date}
                        </Text>
                      </VStack>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Badge
                        colorScheme={
                          transaction.type === "Deposit"
                            ? "green"
                            : transaction.type === "Withdrawal"
                            ? "red"
                            : "purple"
                        }
                      >
                        {transaction.type}
                      </Badge>
                    </td>
                    <td style={{ padding: '12px', fontWeight: 'medium' }}>
                      {transaction.amount}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Badge
                        colorScheme={
                          transaction.status === "Completed"
                            ? "green"
                            : transaction.status === "Pending"
                            ? "yellow"
                            : "blue"
                        }
                      >
                        {transaction.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>

        {/* Top Performers */}
        <Box bg="white" p={5} borderRadius="xl" boxShadow="md">
          <HStack justify="space-between" mb={4}>
            <Heading size="md">Top Performers</Heading>
            <Icon as={FaChartLine} color="green.500" />
          </HStack>
          <VStack spacing={3} align="stretch">
            {topPerformers.map((performer) => (
              <HStack
                key={performer.rank}
                p={3}
                borderRadius="md"
                bg="gray.50"
                _hover={{ bg: "gray.100" }}
              >
                <Box
                  w={8}
                  h={8}
                  borderRadius="full"
                  bg={
                    performer.rank === 1
                      ? "gold.400"
                      : performer.rank === 2
                      ? "gray.400"
                      : performer.rank === 3
                      ? "orange.400"
                      : "gray.200"
                  }
                  color="white"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="bold"
                >
                  {performer.rank}
                </Box>
                <Box flex={1}>
                  <Text fontWeight="medium">{performer.name}</Text>
                  <Text fontSize="xs" color="gray.500">
                    {performer.trades} trades
                  </Text>
                </Box>
                <Text fontWeight="bold" color="green.600">
                  {performer.profit}
                </Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      </SimpleGrid>

      {/* Additional Metrics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
        <Box bg="white" p={5} borderRadius="xl" boxShadow="md">
          <HStack spacing={4}>
            <Box
              p={3}
              borderRadius="full"
              bg="blue.100"
              color="blue.600"
            >
              <Icon as={FaDollarSign} boxSize={6} />
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Total Balance
              </Text>
              <Text fontSize="xl" fontWeight="bold">
                $1,234,567
              </Text>
            </Box>
          </HStack>
        </Box>

        <Box bg="white" p={5} borderRadius="xl" boxShadow="md">
          <HStack spacing={4}>
            <Box
              p={3}
              borderRadius="full"
              bg="purple.100"
              color="purple.600"
            >
              <Icon as={FaChartLine} boxSize={6} />
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Avg. Trade Size
              </Text>
              <Text fontSize="xl" fontWeight="bold">
                $2,450
              </Text>
            </Box>
          </HStack>
        </Box>

        <Box bg="white" p={5} borderRadius="xl" boxShadow="md">
          <HStack spacing={4}>
            <Box
              p={3}
              borderRadius="full"
              bg="green.100"
              color="green.600"
            >
              <Icon as={FaClock} boxSize={6} />
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Pending Actions
              </Text>
              <Text fontSize="xl" fontWeight="bold">
                23
              </Text>
            </Box>
          </HStack>
        </Box>

        <Box bg="white" p={5} borderRadius="xl" boxShadow="md">
          <HStack spacing={4}>
            <Box
              p={3}
              borderRadius="full"
              bg="orange.100"
              color="orange.600"
            >
              <Icon as={FaCheckCircle} boxSize={6} />
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                Success Rate
              </Text>
              <Text fontSize="xl" fontWeight="bold">
                98.5%
              </Text>
            </Box>
          </HStack>
        </Box>
      </SimpleGrid>
    </VStack>
  );
};